"use client";

import Image from "next/image";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import {ChevronLeft, ChevronRight, Instagram} from "lucide-react";
import {useTranslations} from "next-intl";
import {useEffect, useRef, useState} from "react";
import {TINY_BLUR_DATA_URL} from "@/lib/image-blur";

const GALLERY_SRC = ["/gallery/pyria.png", "/gallery/cleaning.png", "/gallery/restoration.png"] as const;

export function SmileGallery() {
  const t = useTranslations("gallery");
  const slides = [1, 2, 3].map((i) => ({
    src: GALLERY_SRC[i - 1],
    alt: t(`slides.${i}.alt`),
    caption: t(`slides.${i}.caption`),
  }));
  const autoplay = useRef(
    Autoplay({
      delay: 3200,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    }),
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "center",
      loop: true,
      skipSnaps: false,
      duration: 32,
    },
    [autoplay.current],
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi]);

  return (
    <section
      id="gallery"
      className="clinic-gallery border-y border-[#173b35]/8 bg-[#efede6] px-4 py-16 text-[var(--clinic-ink)] dark:border-white/8 dark:bg-[#0d1d1b] sm:px-6 md:py-24 lg:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <p className="clinic-eyebrow">
          {t("eyebrow")}
        </p>
        <h2 className="clinic-display clinic-heading">
          {t("title")}
        </h2>

        <div className="relative mt-8 overflow-hidden py-2">
          <button
            type="button"
            onClick={() => emblaApi?.scrollPrev()}
            aria-label="Previous gallery image"
            className="absolute left-1 top-1/2 z-20 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-[#173b35]/15 bg-[#fffefa]/90 text-[var(--clinic-ink)] shadow-lg backdrop-blur-md transition hover:bg-white dark:border-white/15 dark:bg-[#173b35]/90 dark:text-white sm:left-3"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => emblaApi?.scrollNext()}
            aria-label="Next gallery image"
            className="absolute right-1 top-1/2 z-20 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-[#173b35]/15 bg-[#fffefa]/90 text-[var(--clinic-ink)] shadow-lg backdrop-blur-md transition hover:bg-white dark:border-white/15 dark:bg-[#173b35]/90 dark:text-white sm:right-3"
          >
            <ChevronRight className="h-5 w-5" aria-hidden />
          </button>
          <div ref={emblaRef} className="overflow-visible">
            <div className="-ml-3 flex touch-pan-y">
              {slides.map((image, index) => {
                const isActive = selectedIndex === index;

                return (
                  <div
                    key={image.src}
                    className="min-w-0 flex-[0_0_86%] pl-3 sm:flex-[0_0_74%] lg:flex-[0_0_46%]"
                  >
                    <article
                      className={`overflow-hidden rounded-[1.6rem] border bg-[#fffefa] shadow-[0_20px_46px_-30px_rgba(23,59,53,0.5)] transition-all duration-500 dark:bg-[#173b35] ${
                        isActive
                          ? "scale-100 border-[#173b35]/15 blur-0 opacity-100 dark:border-white/15"
                          : "scale-95 border-[var(--clinic-line-strong)] blur-[3px] opacity-70"
                      }`}
                    >
                      <figure className="m-0">
                        <div className="aspect-[4/3] w-full">
                          <Image
                            src={image.src}
                            alt={image.alt}
                            width={1200}
                            height={900}
                            className="h-full w-full object-cover"
                            sizes="(min-width: 1024px) 46vw, (min-width: 640px) 74vw, 86vw"
                            priority={index === 0}
                            loading={index === 0 ? "eager" : "lazy"}
                            placeholder="blur"
                            blurDataURL={TINY_BLUR_DATA_URL}
                          />
                        </div>
                        <figcaption className="border-t border-[var(--clinic-line)] bg-[var(--clinic-surface-soft)] px-3 py-2 text-center text-xs leading-snug text-[var(--clinic-muted)]">
                          {image.caption}
                        </figcaption>
                      </figure>
                    </article>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="mt-8 flex justify-center">
          <a
            href="https://www.instagram.com/clinicbloomingdental/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-12 items-center gap-2 rounded-full bg-[#173b35] px-6 text-sm font-semibold text-white shadow-[0_14px_30px_-18px_rgba(23,59,53,0.5)] transition hover:-translate-y-0.5 hover:bg-[#24534b] dark:bg-[var(--clinic-accent-fill)] dark:hover:bg-[var(--clinic-accent-fill-hover)]"
          >
            <Instagram className="h-4 w-4" aria-hidden />
            {t("instagramCta")}
          </a>
        </div>
      </div>
    </section>
  );
}
